import { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import RightPanelCard from "components/Common/RightPanelCard";
import {
  addTechnicalReport,
  editTechnicalReport,
  deleteTechnicalReportMedia,
} from "actions/job";
import {
  MAX_FILE_SIZE,
  MAX_VIDEO_SIZE,
  ALLOWED_VIDEO_TYPES,
  ALLOWED_IMG_TYPES,
} from "components/Common/constants";
import { showAlert } from "components/Common/commonFunctions";
import { convertHeicIfNeeded } from "components/Common/commonFunctions";

const initialFields = {
  serialNo: {
    value: "",
    error: "",
    isChanged: false,
  },
  technicianID: {
    value: "",
    error: "",
    isChanged: false,
  },
  otherFinding: {
    value: "",
    error: "",
    isChanged: false,
  },
  causeOfFailure: {
    value: "",
    error: "",
    isChanged: false,
  },
  additionalInfo: {
    value: "",
    error: "",
    isChanged: false,
  },
  media: {
    value: [],
    error: "",
    isChanged: false,
  },
};

export function AddTechnicalReport({
  isOpen,
  handleClose,
  decodedId,
  notify,
  technicalReportDetails,
  technicianID,
  techRepoFormData,
}) {
  const [reportData, setReportData] = useState(initialFields);
  const [loaderState, setLoaderState] = useState({
    save: false,
  });
  const [processingMediaCount, setProcessingMediaCount] = useState(0);

  useEffect(() => {
    if (isOpen && techRepoFormData?.ID?.value) {
      setReportData((prev) => ({
        ...prev,
        serialNo: {
          ...prev.serialNo,
          value: techRepoFormData?.serialNo?.value || "",
        },
        otherFinding: {
          ...prev.otherFinding,
          value: techRepoFormData?.otherFinding?.value || "",
        },
        causeOfFailure: {
          ...prev.causeOfFailure,
          value: techRepoFormData?.causeOfFailure?.value || "",
        },
        additionalInfo: {
          ...prev.additionalInfo,
          value: techRepoFormData?.additionalInfo?.value || "",
        },
        media: {
          ...prev.media,
          value: [
            ...(techRepoFormData?.media?.map((item) => {
              const url = item.mediaPath;
              const extension = url.split(".").pop()?.toLowerCase();

              return {
                ID: item.ID,
                preview: url,
                name: url.split("/").pop(),
                type: [
                  "mp4",
                  "mov",
                  "m4v",
                  "mkv",
                  "x-matroska",
                  "matroska",
                ].includes(extension)
                  ? `video/${extension}`
                  : `image/${extension}`,
                file: null,
                existing: true,
              };
            }) || []),
            ...prev.media.value.filter((item) => !item.existing),
          ],
        },
      }));
    }
  }, [techRepoFormData, isOpen]);

  // const convertHeicIfNeeded = async (file) => {
  //   if (
  //     file.type === "image/heic" ||
  //     file.type === "image/heif" ||
  //     file.name.toLowerCase().endsWith(".heic") ||
  //     file.name.toLowerCase().endsWith(".heif")
  //   ) {
  //     const convertedBlob = await heic2any({
  //       blob: file,
  //       toType: "image/jpeg",
  //       quality: 0.9,
  //     });

  //     return new File(
  //       [convertedBlob],
  //       file.name.replace(/\.(heic|heif)$/i, ".jpg"),
  //       { type: "image/jpeg" },
  //     );
  //   }

  //   return file;
  // };

  const getValidationError = (name, value) => {
    const isEmpty = value === "";

    switch (name) {
      case "serialNo":
        return isEmpty ? "Please enter a serial number" : "";

      case "otherFinding":
        return !value ? "Please enter a findings" : "";

      case "causeOfFailure":
        return !value ? "Please enter a causes" : "";

      case "additionalInfo":
        return !value ? "Please enter a actions" : "";

      default:
        return "";
    }
  };

  const validateForm = () => {
    let newUser = { ...reportData };

    Object.keys(newUser).forEach((key) => {
      newUser[key] = {
        ...newUser[key],
        error: getValidationError(key, newUser[key].value),
      };
    });

    setReportData(newUser); // set the error state

    return Object.values(newUser).every((item) => item.error === "");
  };

  const handleOnBlur = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmed);
    setReportData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmed,
        error,
      },
    }));
  };

  const handleChange = (fieldName, newValue) => {
    setReportData((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        value: newValue,
        isChanged: true,
        error: "",
      },
    }));
  };

  const handleChangeMedia = (fieldName, newFiles) => {
    setReportData((prev) => {
      const existingFiles = prev[fieldName]?.value || [];

      const validImages = [];
      const validVideos = [];

      newFiles.forEach((f) => {
        // IMAGE validation
        if (ALLOWED_IMG_TYPES.includes(f.type)) {
          if (f.file.size > MAX_FILE_SIZE) {
            notify("warning", `${f.name} cannot exceed 10 MB (image limit)`);
          } else {
            validImages.push(f);
          }
        }

        // VIDEO validation
        else if (ALLOWED_VIDEO_TYPES.includes(f.type)) {
          if (f.file.size > MAX_VIDEO_SIZE) {
            notify("warning", `${f.name} cannot exceed 50 MB (video limit)`);
          } else {
            validVideos.push(f);
          }
        }

        // Unsupported file
        else {
          notify(
            "warning",
            `${f.name} is not allowed. Only JPG, JPEG, PNG, HEIC, HEIF, WEBP images and MP4, MOV, M4V, MKV videos are allowed.`,
          );
        }
      });

      // Count existing files

      const imagesCount = existingFiles.filter((f) =>
        ALLOWED_IMG_TYPES.includes(f.type),
      ).length;

      const videoCount = existingFiles.filter((f) =>
        ALLOWED_VIDEO_TYPES.includes(f.type),
      ).length;

      // Enforce max counts
      let newImages = validImages;
      let newVideos = validVideos;

      if (imagesCount + newImages.length > 15) {
        newImages = newImages.slice(0, 15 - imagesCount);
        notify("danger", "Cannot add more than 15 images");
      }

      if (videoCount + newVideos.length > 5) {
        newVideos = newVideos.slice(0, 5 - videoCount);
        notify("danger", "Cannot add more than 5 videos");
      }

      return {
        ...prev,
        [fieldName]: {
          ...prev[fieldName],
          value: [...existingFiles, ...newImages, ...newVideos],
          error: "",
          isChanged: true,
        },
      };
    });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoaderState((prev) => ({
        ...prev,
        save: true,
      }));
      const formData = new FormData();
      const bodyData = techRepoFormData?.ID?.value
        ? Object.keys(reportData).reduce(
          (acc, key) => {
            if (key !== "media" && reportData[key].isChanged) {
              acc[key] = reportData[key].value;
            }
            return acc;
          },
          { jobID: decodedId },
        )
        : {
          jobID: decodedId,
          technicianID, //we need to rmeove this from request in future
          serialNo: reportData.serialNo.value,
          causeOfFailure: reportData.causeOfFailure.value,
          otherFinding: reportData.otherFinding.value,
          additionalInfo: reportData.additionalInfo.value,
        };

      formData.append("technicalReport", JSON.stringify(bodyData));
      if (reportData.media?.value?.length > 0) {
        reportData.media.value.forEach((item) => {
          if (!item.file) return;

          if (item.type.startsWith("image/")) {
            formData.append("image", item.file);
          } else if (item.type.startsWith("video/")) {
            formData.append("video", item.file);
          }
        });
      }

      const res = techRepoFormData?.ID?.value
        ? await editTechnicalReport(techRepoFormData.ID.value, formData)
        : await addTechnicalReport(formData);

      if (res?.code === 200) {
        notify(
          "success",
          res.message ||
          (techRepoFormData?.ID?.value
            ? "Technical Report updated successfully"
            : "Technical Report added successfully"),
        );

        handleOnClose();
        technicalReportDetails(decodedId);
      } else {
        notify("danger", res?.message || "Failed to add technical report.");
      }
    } catch (err) {
      notify(
        "danger",
        err?.response?.data?.message ||
        err?.message ||
        "Failed to add technical report.",
      );
    } finally {
      setLoaderState((prev) => ({
        ...prev,
        save: false,
      }));
    }
  };

  const handleOnClose = () => {
    handleClose();
    setReportData(initialFields);
  };

  const handleDeleteMediaItem = async (item, techRepoFormData) => {
    try {
      // setLoaderState((prev) => ({
      //   ...prev,
      //   save: true,
      // }));

      const res = await deleteTechnicalReportMedia(
        item.ID,
        techRepoFormData?.ID?.value,
      );

      if (res?.code !== 200) {
        notify(
          "danger",
          res?.message || "Failed to delete media",
        );
        return;
      }

      notify(
        "success",
        res?.message ||
        "Media deleted successfully",
      );

      await technicalReportDetails(decodedId);
    } catch (err) {
      notify("danger", err?.message || "Failed to delete media");
      return;
    } finally {
      // setLoaderState((prev) => ({
      //   ...prev,
      //   save: false,
      // }));
    }
  }

  return (
    <RightPanelCard isOpen={isOpen} onClose={handleOnClose}>
      <Card className="mb-0 technical-report-card">
        <CardHeader
          className="d-flex align-items-center justify-content-between"
          tag="h4"
        >
          {techRepoFormData?.ID?.value
            ? "Edit Technical Report"
            : "Add Technical Report"}
          <i
            className="fa fa-close text-muted pointer"
            onClick={handleOnClose}
          />
        </CardHeader>
        <hr />
        <CardBody className="technical-report-card-body">
          <Row>
            <Col md="6" className="mb-3">
              <FormGroup
                className={`has-label ${reportData.serialNo.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
              >
                <Label>
                  Serial Number <span className="validation-error">*</span>
                </Label>
                <Input
                  name="serialNo"
                  type="text"
                  placeholder="Enter serial number"
                  maxLength={255}
                  value={reportData.serialNo.value}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                />
                {reportData.serialNo.error && (
                  <small className="validation-error">
                    {reportData.serialNo.error}
                  </small>
                )}
              </FormGroup>
            </Col>

            <Col md="6" className="mb-3">
              <FormGroup
                className={`has-label ${reportData.otherFinding.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
              >
                <Label>
                  Findings <span className="validation-error">*</span>
                </Label>
                <Input
                  name="otherFinding"
                  type="textarea"
                  placeholder="Please describe the findings"
                  maxLength={255}
                  value={reportData.otherFinding.value}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                />
                {reportData.otherFinding.error && (
                  <small className="validation-error">
                    {reportData.otherFinding.error}
                  </small>
                )}
              </FormGroup>
            </Col>

            <Col md="6" className="mb-2">
              <FormGroup
                className={`has-label ${reportData.causeOfFailure.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
              >
                <Label>
                  Cause Of Failure <span className="validation-error">*</span>
                </Label>
                <Input
                  name="causeOfFailure"
                  type="textarea"
                  maxLength={255}
                  placeholder="Please describe the cause of failure"
                  value={reportData.causeOfFailure.value}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                />
                {reportData.causeOfFailure.error && (
                  <small className="validation-error">
                    {reportData.causeOfFailure.error}
                  </small>
                )}
              </FormGroup>
            </Col>

            <Col md="6" className="mb-3">
              <FormGroup
                className={`has-label ${reportData.additionalInfo.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
              >
                <Label>
                  Other Actions <span className="validation-error">*</span>
                </Label>
                <Input
                  name="additionalInfo"
                  type="textarea"
                  maxLength={255}
                  placeholder="Please describe the other actions"
                  value={reportData.additionalInfo.value}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                />
                {reportData.additionalInfo.error && (
                  <small className="validation-error">
                    {reportData.additionalInfo.error}
                  </small>
                )}
              </FormGroup>
            </Col>

            <Col md="12" className="mb-3">
              <FormGroup
                className={`has-label ${reportData.media.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
              >
                <Label>Upload Media</Label>
                <Label className="mb-1 text-muted ">(Accepted formats: Images (PNG, JPEG, JPG, HEIC, HEIF, WEBP - 10 MB max), Videos (MP4, AVI, MOV, MKV, M4V - 50 MB max))</Label>
                <div
                  className="product-image-upload w-100 p-3 border rounded text-center"
                  onDragOver={(e) => e.preventDefault()} // allow drop
                  onDragEnter={(e) => e.preventDefault()}
                  onDrop={async (e) => {
                    e.preventDefault();
                    const files = Array.from(e.dataTransfer.files);
                    setProcessingMediaCount((prev) => prev + files.length);

                    const processedFiles = await Promise.all(
                      files.map(async (file) => {
                        try {
                          const finalFile = await convertHeicIfNeeded(file);

                          return {
                            file: finalFile,
                            preview: URL.createObjectURL(finalFile),
                            name: finalFile.name,
                            type: finalFile.type,
                          };
                        } finally {
                          setProcessingMediaCount((prev) => prev - 1);
                        }
                      }),
                    );

                    if (processedFiles.length > 0) {
                      handleChangeMedia("media", processedFiles);
                    }
                  }}
                >
                  {reportData.media?.value?.length > 0 ? (
                    <div className="d-flex flex-wrap justify-content-start align-items-center">
                      {reportData.media.value.map((item, index) => (
                        <div
                          className="image-preview-wrapper mr-2 position-relative"
                          key={item.preview || item.name}
                        >
                          {/* --- preview: VIDEO / IMAGE --- */}
                          {item?.type?.startsWith("video/") ? (
                            <div className="video-preview-wrapper position-relative">
                              {/* video as imagesCount */}
                              <video
                                className="product-preview-video"
                                src={item.preview}
                                preload="metadata"
                                autoPlay={false}
                                controls={true}
                              />
                              {/* visual play overlay */}
                              {/* <span className="video-play-btn position-absolute">
                                <i className="fa fa-play" />
                              </span> */}
                            </div>
                          ) : (
                            <img
                              src={item.preview}
                              alt={`Product ${index}`}
                              className="product-preview-img"
                            />
                          )}
                          <span
                            className="remove-image position-absolute"
                            onClick={async () => {
                              const item = reportData.media.value[index];
                              if (item?.existing && item?.ID) {
                                showAlert({
                                  title: "Are you sure you want to delete this media?",
                                  type: "warning",
                                  message: "This action cannot be undone.",
                                  confirmStyle: "danger",
                                  cancelStyle: "secondary",
                                  confirmText: "Yes, Delete",
                                  cancelText: "No",
                                  onConfirm: () => handleDeleteMediaItem(item, techRepoFormData),
                                });

                              } else if (item?.file && item?.preview) {
                                URL.revokeObjectURL(item.preview);

                                setReportData((prev) => ({
                                  ...prev,
                                  media: {
                                    ...prev.media,
                                    value: prev.media.value.filter(
                                      (_, i) => i !== index,
                                    ),
                                    error: "",
                                    isChanged: true,
                                  },
                                }));
                              }
                            }}
                          >
                            &times;
                          </span>
                          <p
                            className="file-name mt-2 text-truncate"
                            title={item.name}
                          >
                            {item?.file?.name}
                          </p>
                        </div>
                      ))}
                      {Array.from({ length: processingMediaCount }).map(
                        (_, index) => (
                          <div
                            key={`processing-${index}`}
                            className="image-preview-wrapper mr-2 position-relative"
                          >
                            <div className="product-preview-img processing-media-tile d-flex justify-content-center align-items-center">
                              <i className="fa fa-spinner fa-spin fa-2x text-muted" />
                            </div>

                            <p className="file-name mt-2 invisible">
                              placeholder
                            </p>
                          </div>
                        ),
                      )}

                      <div
                        className="add-more-container"
                        onClick={() => {
                          document.getElementById("productImageInput").click();
                        }}
                      >
                        <div className="add-more-file-wrapper">+</div>
                        <span className="small">Add more</span>
                      </div>
                    </div>
                  ) : (
                    // Show initial instructions when no files uploaded
                    <div className="d-flex flex-column align-items-center">
                      {processingMediaCount > 0 ? (
                        <>
                          <i className="fa fa-spinner fa-spin fa-2x text-muted mb-2" />
                        </>
                      ) : (
                        <>
                          <i className="fa fa-cloud-upload fa-2x text-muted mb-2" />
                          <p className="mb-1 text-muted">
                            Click or drag files (Images/Videos) to upload
                          </p>
                        </>
                      )}
                      {processingMediaCount === 0 && (
                        <Label
                          for="productImageInput"
                          className="btn btn-simple btn-sm mt-2"
                        >
                          Choose Files
                        </Label>
                      )}
                    </div>
                  )}

                  <Input
                    type="file"
                    name="media"
                    multiple
                    accept="image/*,.heic,.heif,video/*"
                    onChange={async (e) => {
                      const selectedFiles = Array.from(e.target.files);

                      setProcessingMediaCount(
                        (prev) => prev + selectedFiles.length,
                      );

                      const processedFiles = await Promise.all(
                        selectedFiles.map(async (file) => {
                          try {
                            const finalFile = await convertHeicIfNeeded(file);

                            return {
                              file: finalFile,
                              preview: URL.createObjectURL(finalFile),
                              name: finalFile.name,
                              type: finalFile.type,
                            };
                          } finally {
                            setProcessingMediaCount((prev) => prev - 1);
                          }
                        }),
                      );

                      if (processedFiles.length > 0) {
                        handleChangeMedia("media", processedFiles);
                      }

                      e.target.value = "";
                    }}
                    className="d-none"
                    id="productImageInput"
                  />
                </div>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>

        <CardFooter className="d-flex justify-content-end mb-0">
          <Button color="secondary" onClick={handleOnClose} className="mr-2">
            Cancel
          </Button>
          <Button
            color="default"
            type="submit"
            onClick={handleAddSubmit}
            disabled={
              Object.values(reportData).some((item) => item.error !== "") ||
              processingMediaCount > 0 || loaderState.save
            }
          >
            {loaderState.save ? (
              <i className="fa fa-spinner fa-spin fa-lg text-white" />
            ) : (
              "Save"
            )}
          </Button>
        </CardFooter>
      </Card>
    </RightPanelCard>
  );
}
